import React, { useEffect, useRef, useState } from "react";
import { useDataLayer } from "../../../Contexts/DataLayer.context.jsx";
import { usePlayer } from "../../../Contexts/playerData.context.jsx";
import { useNavBar } from "../../../Contexts/navbar.items.context.jsx";
import { getTokenFromLocalStorage } from "../../../utils/functions.ts";
import { fetchDataQuery } from "../../../utils/functions.ts";
import { getEasternSqlDateTime } from "../../../utils/dateUtils.ts";

import DragBox2026 from "../../Universals/DragBox/DragBox2026/dragBox2026.component.jsx";

// ------------------------ constants ------------------------
const STORAGE_KEY_POS = "SQ_BOX_LOCATION";
const DEFAULT_POS = { x: 57, y: 260 };

// backend (save / rename / delete)
const SQ_ENDPOINT = "https://www.fbxchange.com/ReactPHP/savedQuery.php";

// ------------------------ helpers ------------------------
const nowSqlString = () => getEasternSqlDateTime();

const getName = (q) => q?.listName ?? q?.queryName ?? q?.name ?? "Saved Query";

const getCount = (q) => {
  const v = q?.listTotal ?? q?.total ?? q?.count ?? 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const getCreated = (q) => q?.dateCreated ?? q?.createdAt ?? q?.created ?? "";

const isActive = (q) => String(q?.active ?? q?.Active ?? "Y").toUpperCase() === "Y";

const getQueryId = (q) => String(q?.listId ?? q?.id ?? q?.ID ?? "");

const normalizeSearchType = (value) => {
  const raw = String(value ?? "").trim();

  if (raw === "Note") return "Note";
  if (raw === "Scout" || raw === "Report") return "Scout";
  if (raw === "NoS2" || raw === "Regular") return "NoS2";

  return "NoS2";
};

const displaySearchType = (value) => {
  const type = normalizeSearchType(value);
  if (type === "Note") return "Note";
  if (type === "Scout") return "Scout";
  return "Regular";
};

// ------------------------ component ------------------------
const SavedQueryBox = () => {
  const {
    savedQueryArray,
    updateSavedQueryArray,
    updateWhichOneLiner,
  } = useDataLayer();

  const {
    setPlayerData,
    setListLoadID,
    setSavedOriginalPlayerData,
    setPlayerID,
    setActivePlayer,
  } = usePlayer();

  const {
    updateSearchedListName,
    updateActiveSideButton,
  } = useNavBar();

  const queryNameRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => {
      queryNameRef.current?.focus();
      queryNameRef.current?.select?.();
    }, 50);
    return () => clearTimeout(t);
  }, []);

  const [queryName, setQueryName] = useState("");
  const [saving, setSaving] = useState(false);
  const [hoverKey, setHoverKey] = useState("");
  const [loadingKey, setLoadingKey] = useState("");
  const [showEdit, setShowEdit] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editName, setEditName] = useState("");
  const [editBusy, setEditBusy] = useState(false);

  const applyWhichOneLiner = (searchTypeIn) => {
    const type = normalizeSearchType(searchTypeIn);

    if (type === "Note") {
      updateWhichOneLiner("Note");
      return;
    }

    if (type === "Scout") {
      updateWhichOneLiner("Scout");
      return;
    }

    updateWhichOneLiner(null);
  };

  const handleSave = async () => {
    const name = (queryName || "").trim();
    if (!name) {
      alert("Please enter a Query Name.");
      return;
    }
    if (name.length < 5) return;

    try {
      setSaving(true);
      const user = await getTokenFromLocalStorage();

      const res = await fetch(SQ_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scriptName: "saveQuery",
          userID: String(user?.scout ?? ""),
          team: String(user?.team ?? ""),
          queryName: String(name),
          dateCreated: nowSqlString(),
        }),
      });

      const result = await res.json();
      if (!result?.success) {
        throw new Error(result?.message || "Failed to save query");
      }

      if (Array.isArray(result?.queries)) {
        updateSavedQueryArray(result.queries);
      } else {
        updateSavedQueryArray((prev) => {
          const arr = Array.isArray(prev) ? prev : [];
          return [
            ...arr,
            {
              listId: result?.listId ?? result?.id ?? "",
              listName: name,
              listTotal: 0,
              dateCreated: nowSqlString(),
              active: "Y",
              searchType: "NoS2",
            },
          ];
        });
      }

      setQueryName("");
      queryNameRef.current?.focus();
    } catch (err) {
      console.error("Failed to save query", err);
    } finally {
      setSaving(false);
    }
  };

  const listClicked = async (q, key) => {
    if (loadingKey) return;

    try {
      setLoadingKey(String(key || ""));

      const user = await getTokenFromLocalStorage();
      const sentSearchType = normalizeSearchType(q?.searchType);

      // switch OneLiner RIGHT AWAY like S2Box
      applyWhichOneLiner(sentSearchType);

      console.log("--------------------------------------------------");
      console.log("Saved Query clicked:", q);
      console.log("Saved Query listId:", q?.listId);
      console.log("Saved Query name:", getName(q));
      console.log("Saved Query raw searchType:", q?.searchType);
      console.log("Saved Query normalized searchType being sent:", sentSearchType);

      const newPlayers = await fetchDataQuery(
        q?.listId,
        user?.scout,
        user?.team,
        sentSearchType
      );

      console.log("Raw response from fetchDataQuery:", newPlayers);
      console.log(
        "Raw response first row:",
        Array.isArray(newPlayers) ? newPlayers?.[0] : null
      );

      if (!Array.isArray(newPlayers) || newPlayers.length === 0) {
        alert("No players found in this saved query.");
        return;
      }

      // stamp with the already-chosen type so display does not have to guess
      const stampedPlayers = newPlayers.map((player) => ({
        ...player,
        searchType: sentSearchType,
      }));

      setPlayerData(stampedPlayers);
      setSavedOriginalPlayerData(stampedPlayers);
      setPlayerID(stampedPlayers[0]?.FBXID || "");
      setActivePlayer(stampedPlayers[0] || null);
      updateSearchedListName("Saved Query: " + getName(q));
      setListLoadID(Math.floor(Math.random() * 1000000) + 1000);
      updateActiveSideButton("");

      console.log("Saved Query load complete.");
      console.log("--------------------------------------------------");
    } catch (error) {
      console.error("Saved query load failed:", error);
    } finally {
      setLoadingKey("");
    }
  };

  const openEdit = (q) => {
    setEditRow(q);
    setEditName(getName(q));
    setShowEdit(true);
  };

  const closeEdit = () => {
    setShowEdit(false);
    setEditRow(null);
    setEditName("");
    setEditBusy(false);
  };

  const applyRenameLocal = (id, newName) => {
    updateSavedQueryArray((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      return arr.map((x) => {
        const xid = getQueryId(x);
        if (String(xid) !== String(id)) return x;
        return { ...x, listName: newName, queryName: newName, name: newName };
      });
    });
  };

  const applyDeleteLocal = (id) => {
    updateSavedQueryArray((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      return arr.filter((x) => String(getQueryId(x)) !== String(id));
    });
  };

  const handleRename = async () => {
    const q = editRow;
    if (!q) return;

    const id = getQueryId(q);
    const newName = (editName || "").trim();

    if (!id) return;
    if (!newName || newName.length < 5) {
      alert("Name must be at least 5 characters.");
      return;
    }

    try {
      setEditBusy(true);
      const user = await getTokenFromLocalStorage();

      applyRenameLocal(id, newName);

      const res = await fetch(SQ_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scriptName: "renameQuery",
          userID: String(user?.scout ?? ""),
          team: String(user?.team ?? ""),
          listId: String(id),
          queryName: String(newName),
        }),
      });

      const result = await res.json();

      if (!result?.success) {
        throw new Error(result?.message || "Rename failed");
      }

      if (Array.isArray(result?.queries)) {
        updateSavedQueryArray(result.queries);
      }

      closeEdit();
    } catch (err) {
      console.error("Rename failed:", err);
      alert("Rename failed. Check console.");
    } finally {
      setEditBusy(false);
    }
  };

  const handleDelete = async () => {
    const q = editRow;
    if (!q) return;

    const id = getQueryId(q);
    if (!id) return;

    const ok = window.confirm("Delete this saved query?");
    if (!ok) return;

    try {
      setEditBusy(true);
      const user = await getTokenFromLocalStorage();

      applyDeleteLocal(id);

      const res = await fetch(SQ_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scriptName: "deleteQuery",
          userID: String(user?.scout ?? ""),
          team: String(user?.team ?? ""),
          listId: String(id),
        }),
      });

      const result = await res.json();

      if (!result?.success) {
        throw new Error(result?.message || "Delete failed");
      }

      if (Array.isArray(result?.queries)) {
        updateSavedQueryArray(result.queries);
      }

      closeEdit();
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Delete failed. Check console.");
    } finally {
      setEditBusy(false);
    }
  };

  const all = Array.isArray(savedQueryArray) ? savedQueryArray : [];
  const rows = all.filter(isActive);

  return (
    <DragBox2026
      storageKey={STORAGE_KEY_POS}
      defaultPos={DEFAULT_POS}
      title="Saved Queries"
      countText={`[${rows.length} Saved]`}
      minWidth={800}
      maxWidth={800}
      zIndex={500}
      showReset={true}
    >
      <div className="d-flex flex-column" style={{ minHeight: 360, maxHeight: 700 }}>
        <div className="p-1 alert-secondary px-3">
          <div className="p-1 mb-2 fs-7 text-center">
            Save Your Last Search by entering a Query Name and clicking Save / Enter
          </div>

          <div className="row g-2 align-items-center mb-2">
            <div className="col-22">
              <input
                ref={queryNameRef}
                className="form-control form-control-sm"
                placeholder="Enter Query Name… (Max 40 chars)"
                value={queryName}
                onChange={(e) => setQueryName(e.target.value)}
                maxLength={40}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSave();
                }}
              />
            </div>

            <div className="text-muted small col-10 fs-7">
              Used {(queryName || "").length}/40 - Min 5
            </div>

            <div className="col-16 d-flex justify-content-end">
              <div className="col-36">
                <button
                  className="btn btn-sm btn-outline-success w-100"
                  onClick={handleSave}
                  disabled={saving || (queryName || "").trim().length < 5}
                >
                  {saving ? "Saving…" : "Save / Enter"}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div
          className="border rounded p-2"
          style={{ overflowY: "auto", minHeight: 200, maxHeight: 620 }}
        >
          <div className="row g-1 border-bottom pb-1 mb-1 fs-7 px-2 fw-semibold bg-navy text-lemon">
            <div className="col-16">List Name</div>
            <div className="col-8 text-center">Count</div>
            <div className="col-8 text-center">Type</div>
            <div className="col-12">Created</div>
            <div className="col-4 text-center">Edit</div>
          </div>

          {rows.length === 0 ? (
            <div className="text-muted small">
              <em>No saved queries.</em>
            </div>
          ) : (
            rows.map((q, idx) => {
              const name = getName(q);
              const count = getCount(q);
              const created = getCreated(q);
              const id = getQueryId(q);
              const key = String(id || idx);
              const searchType = displaySearchType(q?.searchType);
              const rowHoverClass = hoverKey === key ? "alert-primary" : "";
              const isLoadingThisRow = loadingKey === key;

              return (
                <div
                  key={key}
                  style={{
                    cursor: loadingKey ? "wait" : "pointer",
                    lineHeight: "25px",
                    opacity: loadingKey && !isLoadingThisRow ? 0.55 : 1,
                  }}
                  className={`row g-1 py-1 px-2 border-bottom fs-7 ${rowHoverClass}`}
                  onClick={() => {
                    if (!loadingKey) listClicked(q, key);
                  }}
                  onMouseEnter={() => setHoverKey(key)}
                  onMouseLeave={() => setHoverKey("")}
                  title={isLoadingThisRow ? "Loading…" : ""}
                >
                  <div className="col-16 text-truncate" title={name}>
                    {name}
                    {isLoadingThisRow && (
                      <span className="ms-2 text-muted fw-semibold" style={{ fontSize: 11 }}>
                        Loading…
                      </span>
                    )}
                  </div>

                  <div className="col-8 text-center text-primary fw-semibold">
                    {isLoadingThisRow ? "…" : count}
                  </div>

                  <div className="col-8 text-center">{searchType}</div>

                  <div className="col-12 text-truncate" title={created || ""}>
                    {created || "-"}
                  </div>

                  <div className="col-4 text-center">
                    <button
                      className="btn btn-outline-secondary btn-sm py-0 px-2"
                      style={{ lineHeight: "18px" }}
                      disabled={!!loadingKey}
                      onClick={(e) => {
                        e.stopPropagation();
                        openEdit(q);
                      }}
                      title="Edit saved query"
                    >
                      Edit
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {showEdit && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
          style={{ background: "rgba(0,0,0,0.35)" }}
          onMouseDown={(e) => {
            if (e.target?.classList?.contains("modal")) closeEdit();
          }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content shadow">
              <div className="modal-header">
                <h5 className="modal-title">Edit Saved Query</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeEdit}
                  disabled={editBusy}
                />
              </div>

              <div className="modal-body">
                <div className="row g-2">
                  <div className="col-48">
                    <label className="form-label mb-1">Query Name</label>
                    <input
                      className="form-control form-control-sm"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      maxLength={40}
                      disabled={editBusy}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleRename();
                      }}
                    />
                    <div className="form-text text-muted">
                      Used {(editName || "").length}/40 - Min 5
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer d-flex justify-content-between">
                <button
                  className="btn btn-outline-danger"
                  onClick={handleDelete}
                  disabled={editBusy}
                  title="Delete this saved query"
                >
                  {editBusy ? "Working…" : "Delete"}
                </button>

                <div className="d-flex gap-2">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={closeEdit}
                    disabled={editBusy}
                  >
                    Close
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleRename}
                    disabled={editBusy || (editName || "").trim().length < 5}
                  >
                    {editBusy ? "Saving…" : "Save Name"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </DragBox2026>
  );
};

export default SavedQueryBox;
